'use client';

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from '@/locales/en.json';
import fi from '@/locales/fi.json';
import sv from '@/locales/sv.json';
import de from '@/locales/de.json';
import fr from '@/locales/fr.json';
import es from '@/locales/es.json';
import it from '@/locales/it.json';
import pt from '@/locales/pt.json';
import jp from '@/locales/jp.json';
import zh from '@/locales/zh.json';
import uk from '@/locales/uk.json';
import { addLocale, locale } from 'primereact/api';

const translations = { en, fi, sv, de, fr, es, it, pt, jp, zh, uk };

const TranslationContext = createContext();
const DEFAULT_LANGUAGE = 'en';

const countries =
  Object.entries(translations).reduce((acc, [lang, locs]) => {
    acc[lang] = Object.entries(locs)
      .filter(([code]) => code.startsWith('iso_country_'))
      .map(([code, name]) => ({ code: code.replace('iso_country_', ''), name }))
      .sort((a, b) => {
        if (a.code === 'FI') return -1;
        if (b.code === 'FI') return 1;
        return a.name.localeCompare(b.name);
      });
    return acc;
  }, {});

const usStates =
  Object.entries(translations).reduce((acc, [lang, locs]) => {
    acc[lang] = Object.entries(locs)
      .filter(([code]) => code.startsWith('us_state_'))
      .map(([code, name]) => ({ code: code.replace('us_state_', ''), name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return acc;
  }, {});

const germanStates =
  Object.entries(translations).reduce((acc, [lang, locs]) => {
    acc[lang] = Object.entries(locs)
      .filter(([code]) => code.startsWith('de_state_'))
      .map(([code, name]) => ({ code: code.replace('de_state_', ''), name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return acc;
  }, {});

const canadianProvinces =
  Object.entries(translations).reduce((acc, [lang, locs]) => {
    acc[lang] = Object.entries(locs)
      .filter(([code]) => code.startsWith('ca_province_'))
      .map(([code, name]) => ({ code: code.replace('ca_province_', ''), name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return acc;
  }, {});

// Japanese is 'jp' in this database and 'ja' in every browser, Intl table and ISO list,
// so every code entering the app is folded to the spelling the database uses. Doing it in
// one helper means a Japanese browser gets a Japanese interface as well as Japanese
// descriptions -- matching on the raw 'ja' would miss the 'jp' locale file entirely.
const normalizeCode = tag => {
  const code = (tag || '').split('-')[0].slice(0, 2).toLowerCase();
  return code === 'ja' ? 'jp' : code;
};

// Interface and content now cover the same languages, so this is derived rather than
// listed by hand: a locale file is what decides whether `t` can resolve a key at all, so
// dropping one into locales/ (and importing it above) is the only step needed to offer a
// language. The two values stay separate because they answer separate questions and are
// stored separately -- see changeLanguage.
const CONTENT_LANGUAGES = Object.keys(translations);

// Stored under its own key on purpose. `language` is written back on every mount, so
// after the first visit it always holds an interface language -- reading the content
// language from it would mean the browser's own preference is consulted exactly once
// and then silently lost.
const getContentLanguage = uiLanguage => {
  if (typeof window === 'undefined') {
    return uiLanguage;
  }

  const chosen = normalizeCode(localStorage.getItem('contentLanguage'));
  if (CONTENT_LANGUAGES.includes(chosen)) {
    return chosen;
  }

  const preferred = (navigator.languages || [navigator.language])
    .map(normalizeCode)
    .find(code => CONTENT_LANGUAGES.includes(code));

  return preferred || uiLanguage;
};

const getBrowserLanguage = () => {
  if (typeof window !== 'undefined') {
    // Validated rather than trusted: a code stored before its locale file existed (or
    // after one was removed) would send `t` looking for keys that are not there.
    const stored = normalizeCode(localStorage.getItem('language'));
    if (translations[stored]) {
      return stored;
    }

    const browserLanguages = navigator.languages || [navigator.language];
    return browserLanguages.map(normalizeCode).find(code => translations[code]) || DEFAULT_LANGUAGE;
  }
  return DEFAULT_LANGUAGE;
};

const initializeCalendarLocales = (t) => {
  Object.entries(translations).forEach(([lang, t]) => {
    const loc = {
      dayNames: [
        t['day_name_sunday'],
        t['day_name_monday'],
        t['day_name_tuesday'],
        t['day_name_wednesday'],
        t['day_name_thursday'],
        t['day_name_friday'],
        t['day_name_saturday']],
      dayNamesShort: [
        t['day_name_short_sunday'],
        t['day_name_short_monday'],
        t['day_name_short_tuesday'],
        t['day_name_short_wednesday'],
        t['day_name_short_thursday'],
        t['day_name_short_friday'],
        t['day_name_short_saturday']],
      dayNamesMin: [
        t['day_name_min_sunday'],
        t['day_name_min_monday'],
        t['day_name_min_tuesday'],
        t['day_name_min_wednesday'],
        t['day_name_min_thursday'],
        t['day_name_min_friday'],
        t['day_name_min_saturday']],
      monthNames: [
        t['month_name_january'],
        t['month_name_february'],
        t['month_name_march'],
        t['month_name_april'],
        t['month_name_may'],
        t['month_name_june'],
        t['month_name_july'],
        t['month_name_august'],
        t['month_name_september'],
        t['month_name_october'],
        t['month_name_november'],
        t['month_name_december']],
      monthNamesShort: [
        t['month_name_short_january'],
        t['month_name_short_february'],
        t['month_name_short_march'],
        t['month_name_short_april'],
        t['month_name_short_may'],
        t['month_name_short_june'],
        t['month_name_short_july'],
        t['month_name_short_august'],
        t['month_name_short_september'],
        t['month_name_short_october'],
        t['month_name_short_november'],
        t['month_name_short_december']],
      today: t['cal_today'],
      clear: t['cal_clear'],
      chooseDate: t['cal_choose_date']
    };

    addLocale(`${lang}-0`, {
      firstDayOfWeek: 0,
      ...loc
    });

    addLocale(`${lang}-1`, {
      firstDayOfWeek: 1,
      ...loc
    });

    addLocale(`${lang}-5`, {
      firstDayOfWeek: 5,
      ...loc
    });

    addLocale(`${lang}-6`, {
      firstDayOfWeek: 6,
      ...loc
    });

    addLocale(`${lang}-7`, {
      firstDayOfWeek: 0,
      ...loc
    });
  });
};

initializeCalendarLocales(key => key);

export function TranslationProvider({ children }) {
  const [language, setLanguage] = useState(getBrowserLanguage);
  const [contentLanguage, setContentLanguage] = useState(() => getContentLanguage(getBrowserLanguage()));

  // A stored language preference is a statement about what to read in as well, so it
  // moves both -- the browser's preference only decides for someone who has none. This
  // is called on every page load to sync the signed-in user's profile language, so it
  // has to be stable (an effect elsewhere depends on its identity) and idempotent.
  //
  // The two are still clamped separately even though the sets currently coincide: the
  // interface only exists in the languages there are locale files for, and anything else
  // would send `t` looking up keys that are not there and render them raw. Content is
  // translated by a separate pipeline, so if it ever runs ahead of the locale files a
  // language the interface cannot honour still decides what the descriptions are read in.
  const changeLanguage = useCallback(next => {
    const code = normalizeCode(next);

    setLanguage(translations[code] ? code : DEFAULT_LANGUAGE);

    if (CONTENT_LANGUAGES.includes(code)) {
      setContentLanguage(code);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('contentLanguage', contentLanguage);
  }, [contentLanguage]);

  // The selected language as a tag Intl actually recognises: the database's 'jp' has to
  // become the ISO 'ja', or Intl quietly answers for en-US instead and a Japanese reader
  // gets m/d/yy and weeks starting on Sunday. Exposed because anything asking Intl to
  // format or collate for the current interface language needs this rather than `language`
  // -- the PrimeReact locale names registered by addLocale keep using `language`, so the
  // two stay in step.
  const intlLocale = language === 'jp' ? 'ja' : language;

  const getCalendarFormatLocale = () => {
    // Use the selected language, not browser language
    return intlLocale;
  };

  useEffect(() => {
    localStorage.setItem('language', language);
    // Set PrimeReact locale when language changes
    const resolved = new Intl.Locale(getCalendarFormatLocale());
    const firstDay = resolved.getWeekInfo?.()?.firstDay || resolved.weekInfo?.firstDay || 1;
    locale(`${language}-${firstDay}`);
  }, [language]);

  const getCountries = () => {
    return countries[language];
  };

  const getUsStates = () => {
    return usStates[language];
  };

  const getGermanStates = () => {
    return germanStates[language];
  };

  const getCanadianProvinces = () => {
    return canadianProvinces[language];
  };

  const getCalendarDateFormat = () => {
    return new Intl.DateTimeFormat(getCalendarFormatLocale()).formatToParts()
      .map(part => {
        switch (part.type) {
          case 'day': return 'd';
          case 'month': return 'm';
          case 'year': return 'yy';
          default: return part.value;
        }
      }).join('');
  };

  const getCalendarLocale = () => {
    const locale = new Intl.Locale(getCalendarFormatLocale());
    const firstDay = locale.getWeekInfo?.()?.firstDay || locale.weekInfo?.firstDay || 1;
    return `${language}-${firstDay}`;
  };

  const t = (key, replacements) => {
    let str = translations[language]?.[key] ?? key;

    if (replacements && str) {
      Object.entries(replacements).forEach(([key, value]) => {
        str = str.replaceAll(`{{${key}}}`, value);
      });
    }

    return str;
  };

  const tc = (key, replacements) => {
    let str = translations[language]?.[key] ?? key;

    if (replacements && str) {
      Object.entries(replacements).forEach(([key, value]) => {
        str = str.replaceAll(`{{${key}}}`, value);
      });
    }

    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const tOrNull = (key, replacements) => {
    let str = translations[language]?.[key];

    if (replacements && str) {
      Object.entries(replacements).forEach(([key, value]) => {
        str = str.replaceAll(`{{${key}}}`, value);
      });
    }

    return str;
  };

  return (
    <TranslationContext.Provider value={{
      t,
      tc,
      tOrNull,
      language,
      intlLocale,
      contentLanguage,
      translations,
      setLanguage: changeLanguage,
      getCanadianProvinces,
      getGermanStates,
      getUsStates,
      getCountries,
      getCalendarLocale,
      getCalendarDateFormat
    }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}